import { message } from 'antd';
import { useState, useEffect } from 'react';
import { lazadaVideoUrl } from 'services/api/conversation';
import { useTranslate } from '@tolgee/react';
import chatttSDK from 'services/chattt';
import { useMemoizedFn } from 'ahooks';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { openExternalURL } from 'utils/openExternalURL';
import { VideoPreviewer } from './VideoPreview';
import { modalWrapperClassName } from './VideoModal';

export interface VideoMessageData {
  thumb?: string;
  url: string;
}

export interface VideoMessageProps
  extends React.DetailedHTMLProps<React.VideoHTMLAttributes<HTMLVideoElement>, HTMLVideoElement> {
  className?: string;
  dataSource: VideoMessageData;
  msg: Conversation.Message;
  storeName?: string;
  lazyScrollContainer?: Element | null;
}

const VideoMessage = (props: VideoMessageProps) => {
  const {
    dataSource: { url, thumb },
    className,
    controls = true,
    storeName,
    msg,
    lazyScrollContainer,
  } = props;
  const {
    msgId,
    conversationId,
    channel,
    fromUserName,
    videoContent: { videoId, expireTimestamp, codecType, localVideoUrl } = {},
  } = msg;
  const { whiteListConfig: { VIDEO_PLAY_ON_BROWSER = false } = {} } = useOrgConfigStateValue();

  const [visible, setVisible] = useState(false);
  const [videoUrl, setVideoUrl] = useState(url);
  const { t } = useTranslate();

  useEffect(() => {
    if (!visible || videoUrl || !videoId) {
      return;
    }

    lazadaVideoUrl({
      msgId,
      videoId,
      conversationId,
    })
      .then((res) => {
        setVideoUrl(res.videoUrl);
      })
      .catch(() => {
        message.error(t('ViewsChat.获取视频地址失败，请稍后再试'));
      });
  }, [visible, videoUrl, msgId, videoId, conversationId, t]);

  // 打开应用视频播放器
  const handleOpenVideoPlayer = useMemoizedFn(() => {
    if (VIDEO_PLAY_ON_BROWSER) {
      openExternalURL(videoUrl, '_blank');
    } else if (chatttSDK?.videoPlayer?.open) {
      const title = [fromUserName, storeName ?? '', channel].join('-');
      chatttSDK?.videoPlayer.open({
        title,
        poster: thumb,
        source: videoUrl,
        debug: localStorage.getItem('videoPlayerDebug') === 'true',
        transofrmCodec: codecType === 'hevc',
      });
    }
  });

  return (
    <VideoPreviewer
      className={className}
      thumb={thumb}
      url={videoUrl}
      localVideoUrl={localVideoUrl}
      controls={controls}
      expireTimestamp={expireTimestamp}
      channel={channel}
      msg={msg}
      onVideoUrlUpdate={setVideoUrl}
      onVisibleChange={setVisible}
      onClick={(e) => e?.stopPropagation()}
      onOpenVideoPlayer={handleOpenVideoPlayer}
      lazyScrollContainer={lazyScrollContainer}
    />
  );
};

export default VideoMessage;

export { modalWrapperClassName };
